import { download, generateCsv, mkConfig } from "export-to-csv";
import { PaperDetails } from "./api";
import NameParser from "./nameParser";
import { detailPagePath } from "./pageUrl";

export type ExportData = {
  Title: string;
  Takeaway: string;
  Authors: string;
  Year: number;
  Citations: number;
  Abstract: string;
  "Study Type": string;
  Journal: string;
  "Journal SJR Quartile": string;
  DOI: string;
  "Consensus Link": string;
};

const csvHeaders = [
  "Title",
  "Takeaway",
  "Authors",
  "Year",
  "Citations",
  "Abstract",
  "Study Type",
  "Journal",
  "Journal SJR Quartile",
  "DOI",
  "Consensus Link",
];

const FILENAME_MAX_LENGTH_CHARS = 300;
const DOWNLOAD_FILENAME_MAX_LENGTH = 50;

function escapeQuotesForCSV(input: string): string {
  return input.replace(/"/g, '""');
}

/**
 * Sanitizes a filename by removing special characters and limiting length
 * @param fileName - The original filename
 * @param maxLength - Maximum length for the filename (default: 50)
 * @returns Sanitized filename
 */
function sanitizeFilename(
  fileName: string,
  maxLength: number = DOWNLOAD_FILENAME_MAX_LENGTH
): string {
  return fileName
    .trim() // Remove leading and trailing whitespace
    .slice(0, maxLength) // Limit the filename length
    .replace(/[^\w\s-]/g, "") // Remove special characters except word chars, spaces, and hyphens
    .replace(/\s+/g, "_"); // Replace spaces with underscores
}

/**
 * Downloads a blob as a file with the specified name and extension
 * @param blob - The blob to download
 * @param fileName - The desired filename (will be sanitized)
 * @param fileTypeSuffix - The file extension (e.g., 'pdf', 'csv', 'txt')
 */
export function exportBlob(
  blob: Blob,
  fileName: string,
  fileTypeSuffix: string
): void {
  // Sanitize the filename and add the extension
  const sanitizedName = sanitizeFilename(fileName);
  const fullFileName = `${sanitizedName}.${fileTypeSuffix}`;

  // Create a download link and trigger the download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fullFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  window.URL.revokeObjectURL(url);
}

function getCSVFilename(title: string) {
  const sanitizedTitle = title
    .replace(/[?*:|"]/g, "")
    .substring(0, FILENAME_MAX_LENGTH_CHARS);
  const date = new Date().toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return `${sanitizedTitle} - ${date}`;
}

export function downloadCSV(data: PaperDetails[], title: string) {
  const filename = getCSVFilename(title);
  const exportData = data.map((item: PaperDetails): ExportData => {
    const quartile =
      item?.journal.scimago_quartile != null
        ? item?.journal.scimago_quartile.toString()
        : "";

    return {
      Title: escapeQuotesForCSV(item.title),
      Takeaway: escapeQuotesForCSV(item.abstract_takeaway),
      Authors: escapeQuotesForCSV(item.authors?.join(", ") ?? ""),
      Year: item.year,
      Citations: item?.citation_count ?? 0,
      Abstract: item?.abstract ? escapeQuotesForCSV(item?.abstract) : "",
      "Study Type": item.badges?.study_type ?? "",
      Journal: item.journal.title,
      "Journal SJR Quartile": quartile,
      DOI: escapeQuotesForCSV(item.doi ?? ""),
      "Consensus Link":
        window.location.origin +
        detailPagePath(item.url_slug ?? "details", item.id),
    };
  });

  const csvConfig = mkConfig({
    columnHeaders: csvHeaders,
    filename: filename,
    quoteStrings: true,
  });
  const csv = generateCsv(csvConfig)(exportData);
  download(csvConfig)(csv);
}

function escapeRISValue(input: string): string {
  return input.replace(/[\r\n]/g, " ").trim();
}

// Get start and end pages from the "pages" property of the paper details response.
// The values in the database require some cleaning up and parsing:
// e.g. "\n        11-9\n        " means pages 11 to 19.
export function parsePages(pages: string): {
  startPage: number | null;
  endPage: number | null;
} {
  // Trim the string to remove leading and trailing whitespace, including "\n"
  const trimmedPages = pages.trim();

  const [start, end] = trimmedPages.split("-").map((part) => part.trim());

  if (!start) {
    return { startPage: null, endPage: null };
  }

  let startPage: number | null = parseInt(start, 10);
  let endPage: number | null = parseInt(end, 10);

  if (isNaN(startPage)) {
    // If the start page isn't a number, return null for both
    return { startPage: null, endPage: null };
  }

  if (!isNaN(endPage)) {
    // Handle abbreviated end page numbers
    if (end.length < start.length) {
      const startPagePrefix = start.slice(0, -end.length);
      endPage = parseInt(startPagePrefix + end, 10);

      if (endPage < startPage) {
        // Handle wrapping centuries, e.g. "1999-05"
        endPage += Math.pow(10, start.length - end.length);
      }
    }
  } else {
    endPage = null;
  }

  return { startPage, endPage };
}

export function formatRisAuthorName(author: string) {
  const parsed = NameParser.parse(author);

  if ((parsed.firstName || parsed.initials) && parsed.lastName) {
    return parsed.lastName + ", " + (parsed.firstName || parsed.initials);
  }
  return parsed.firstName;
}

function paperToRIS(paper: PaperDetails): string {
  const authors =
    paper.authors
      ?.map((author) => `AU  - ${escapeRISValue(formatRisAuthorName(author))}`)
      .join("\n") || "";
  const { startPage, endPage } = parsePages(paper.pages);

  return [
    "TY  - JOUR",
    authors,
    `TI  - ${escapeRISValue(paper.title)}`,
    `PY  - ${paper.year}`,
    `DA  - ${paper.publish_date}`,
    `JO  - ${escapeRISValue(paper.journal.title)}`,
    `VL  - ${paper.volume || ""}`,
    `SP  - ${startPage || ""}`,
    `EP  - ${endPage || ""}`,
    `DO  - ${paper.doi || ""}`,
    `UR  - ${
      window.location.origin +
      detailPagePath(paper.url_slug || "details", paper.id)
    }`,
    "ER  - ",
  ].join("\n");
}

export function downloadRIS(data: PaperDetails[], title: string) {
  const risContent = data.map(paperToRIS).join("\n");

  const filename = `${getCSVFilename(title)}.ris`;
  const blob = new Blob([risContent], { type: "text/plain;charset=utf-8" });

  // Create a URL for the Blob
  const url = URL.createObjectURL(blob);

  // Download the Blob by imitating clicking of an anchor element
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  // Clean up
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
